/**
 * Hook: GET /api/t/{tenantSlug}/interview-prep/history — Phase 2.6.
 *
 * Returns the ordered list of `interview_prep` rows for `(candidate, jd)`,
 * most-recent-first, with stage metadata per row (Phase 2.6 spec §5).
 *
 * Cache key shape: `["interview-prep", "history", tenantSlug, candidateId,
 *                    jdId]`.
 *
 * Hard rules:
 *   - 404 from `history` is the LEGITIMATE empty state (no prior prep
 *     rows yet). Hook catches it and returns `{ rows: [] }`.
 *   - `enabled` gates on `activeTab === "prep"` AND tenant/candidate set.
 *     When `enabled === false`, queryFn is NEVER invoked (LCP guarantee).
 *   - The wire endpoint takes `candidate_id` + optional `jd_id` (per
 *     `app/api/ai.py::interview_prep_history_endpoint`).
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ApiError } from "@/lib/apiError";
import { apiClient } from "@/lib/apiClient";

export interface InterviewPrepHistoryRow {
  id: string;
  candidate_id: string;
  jd_id: string | null;
  stage_id: string | null;
  stage_name: string | null;
  stage_order_index: number | null;
  prompt_version_id: string | null;
  status: string;
  content: string | null;
  model_used: string | null;
  error: string | null;
  created_at: string;
  completed_at: string | null;
}

interface InterviewPrepHistoryResponse {
  rows: InterviewPrepHistoryRow[];
}

interface UseInterviewPrepHistoryOpts {
  candidateId: string | undefined;
  jdId: string | undefined;
  /** Tab-activation gate. */
  enabled: boolean;
}

export function useInterviewPrepHistory(
  opts: UseInterviewPrepHistoryOpts,
): UseQueryResult<InterviewPrepHistoryRow[], ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { candidateId, jdId, enabled } = opts;

  return useQuery<InterviewPrepHistoryRow[], ApiError>({
    queryKey: [
      "interview-prep",
      "history",
      tenantSlug,
      candidateId,
      jdId,
    ],
    queryFn: async () => {
      try {
        const params: Record<string, string> = {};
        if (candidateId) params.candidate_id = candidateId;
        if (jdId) params.jd_id = jdId;
        const r = await apiClient.get<InterviewPrepHistoryResponse>(
          `/api/t/${tenantSlug}/interview-prep/history`,
          { params },
        );
        return r.data.rows ?? [];
      } catch (err) {
        // 404 → empty rows, not error.
        if (err instanceof ApiError && err.status === 404) return [];
        throw err;
      }
    },
    enabled: enabled && !!tenantSlug && !!candidateId,
    refetchInterval: false,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 2;
    },
    staleTime: 30_000,
  });
}
